/** SUNTRONIX ONE — Preflight Retry Ledger viewer.
 *
 *  Managers open this to see which rules chase operators most and
 *  whether a hard block should be softened to approval-required.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { RefreshCcw, ShieldAlert } from "lucide-react";

const KIND_TONE = {
  block:                "bg-red-100 text-red-800",
  retry:                "bg-sky-100 text-sky-800",
  retry_resolved:       "bg-emerald-100 text-emerald-800",
  retry_blocked_again:  "bg-orange-100 text-orange-800",
};

export default function PreflightLedger() {
  const [rows, setRows] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [list, board] = await Promise.all([
        api.get("/preflight-ledger", { params: { limit: 200 } }),
        api.get("/preflight-ledger/worst-offenders", { params: { days } }),
      ]);
      setRows(list.data.items || []);
      setLeaderboard(board.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const topRule = useMemo(() =>
    leaderboard[0]?.name || leaderboard[0]?.rule_id || "—", [leaderboard]);

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="preflight-ledger">
      <PageHeader
        eyebrow="Preflight · Operations"
        title="Preflight Retry Ledger"
        context={`Which rules chase operators most.  Top offender in the last ${days} days: ${topRule}.`}
        actions={
          <div className="flex items-center gap-2">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
              className="border rounded h-9 px-2 text-[12px]" data-testid="pfl-window">
              {[7, 30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
            <Button variant="outline" onClick={load} disabled={busy}>
              <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        }
      />

      <Section title="Worst offender rules" testId="pfl-leaderboard">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Rule</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-right px-3 py-2">Blocks</th>
                <th className="text-right px-3 py-2">Retries</th>
                <th className="text-right px-3 py-2">Resolved</th>
                <th className="text-right px-3 py-2">Re-blocked</th>
                <th className="text-right px-3 py-2">Unique entities</th>
                <th className="text-right px-3 py-2">Resolution %</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((r) => (
                <tr key={r.rule_id} className="border-t border-slate-100"
                      data-testid={`pfl-rule-${r.rule_id}`}>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.name || r.rule_id}</td>
                  <td className="px-3 py-2 text-[11px]">{(r.category || "").replace(/_/g, " ")}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-800">{r.blocks}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-sky-800">{r.retries}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-800">{r.resolved}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-orange-800">{r.re_blocked}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.unique_entities}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{r.resolution_rate}%</td>
                </tr>
              ))}
              {leaderboard.length === 0 && !busy && (
                <tr><td colSpan={8} className="text-center py-8 text-[12px] text-slate-400">
                  No preflight blocks in this window — nothing chasing operators today.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Recent ledger rows" testId="pfl-rows">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">At</th>
                <th className="text-left px-3 py-2">Actor</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-left px-3 py-2">Entity</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Rules</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top">
                  <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">{r.at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-[11px]">{r.actor_email || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest font-semibold ${KIND_TONE[r.kind] || "bg-slate-100"}`}>
                      {r.kind?.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[11px] font-mono">{r.entity_kind || "—"}/{r.entity_id?.slice(0, 8) || "—"}</td>
                  <td className="px-3 py-2 text-[11px]">{r.action || "—"}</td>
                  <td className="px-3 py-2 text-[11px]">
                    {(r.failed_checks || []).map((f) => f.name || f.rule_id).join(" · ") || "—"}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !busy && (
                <tr><td colSpan={6} className="text-center py-8 text-[12px] text-slate-400">
                  <ShieldAlert className="h-5 w-5 mx-auto mb-2 text-slate-300" />
                  Ledger is empty — no preflight blocks tracked yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
